import { MerklRewardLegacy } from "@silo/types";
import { BorrowTotal } from "./borrow-total";
import { DepositTotal } from "./deposit-total";
import { BorrowAprBreakdown } from "./borrow-apr-breakdown";
import { DepositAprBreakdown } from "./deposit-apr-breakdown";
import {
  GetBorrowAprMetricsParams,
  getBorrowAprMetrics,
  getBorrowState,
  getDepositAprMetrics,
} from "./utils";

export interface AssetCardDepositProps {
  depositApr: bigint;
  depositRewardsApr: bigint;
  rewardsSymbol: string;
  isProtected: boolean;
  merklRewards: MerklRewardLegacy[];
}

export function AssetCardDeposit({
  depositApr,
  depositRewardsApr,
  rewardsSymbol,
  isProtected,
  merklRewards,
}: AssetCardDepositProps): JSX.Element {
  const { hasDepositRewardsApr, depositRewards } = getDepositAprMetrics({
    depositApr,
    depositRewardsApr,
    isProtected,
    rewardsSymbol,
    merklRewards,
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <dt className="text-sm text-shale-200">Total deposit APR</dt>

        <DepositTotal
          depositApr={depositApr}
          depositRewardsApr={depositRewardsApr}
          data-cy="deposit-apy"
          isProtected={isProtected}
          rewardsSymbol={rewardsSymbol}
          merklRewards={merklRewards}
        />
      </div>

      {hasDepositRewardsApr && (
        <DepositAprBreakdown
          depositApr={depositApr}
          isProtected={isProtected}
          rewards={depositRewards}
        />
      )}
    </div>
  );
}

export interface AssetCardBorrowProps extends GetBorrowAprMetricsParams {
  isBorrowAprFixed: boolean;
}

export function AssetCardBorrow({
  borrowApr,
  borrowRewardsApr,
  rewardsSymbol,
  isBorrowAprFixed,
  merklRewards,
}: AssetCardBorrowProps): JSX.Element {
  const { hasBorrowRewardsApr, totalBorrowApr, borrowRewards } =
    getBorrowAprMetrics({
      borrowApr,
      borrowRewardsApr,
      rewardsSymbol,
      merklRewards,
    });
  const borrowState = getBorrowState(hasBorrowRewardsApr, totalBorrowApr);

  return (
    <div className="flex gap-2.5 flex-col">
      <div className="flex items-center justify-between">
        <dt className="text-sm text-shale-200">Total borrow APR</dt>

        <BorrowTotal.Root
          className="flex flex-col items-end"
          hasBorrowRewards={hasBorrowRewardsApr}
          totalBorrowApr={totalBorrowApr}
          isBorrowAprFixed={isBorrowAprFixed}
          data-cy="borrow-apy"
        >
          <BorrowTotal.Value totalBorrowApr={totalBorrowApr} />

          {isBorrowAprFixed && <BorrowTotal.Fixed className="text-sm" />}
        </BorrowTotal.Root>
      </div>

      {hasBorrowRewardsApr && (
        <BorrowAprBreakdown
          borrowApr={borrowApr}
          borrowState={borrowState}
          variant="asset"
          rewards={borrowRewards}
        />
      )}
    </div>
  );
}
